import { Injectable } from "@angular/core";
import { AdvancedReportService, Status, translateCommonErrorStatus } from "@core/data";
import { AdvancedReport, ReportAccess } from "@core/data/types/advanced-report";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { tap } from "rxjs/operators";

export interface ReportShareFormData {
    emails: string[],
    access: ReportAccess,
}

export interface AdvancedReportShareDialogStateModel {
    key: string;
    ownerOrgKey: string;
    data: AdvancedReport;
    status: Status;
}

const DEFAULTS: AdvancedReportShareDialogStateModel = {
    key: null,
    ownerOrgKey: null,
    data: null,
    status: Status.UNINITIALIZED,
}

export class InitAdvancedReportShareDialog {
    static readonly type = "[AdvancedReportShareDialogState] Init";
    constructor(public key: string, public ownerOrgKey: string) { }
}

export class SubmitShareDialogData {
    static readonly type = "[AdvancedReportShareDialogState] Submit";
    constructor(public data: ReportShareFormData) { }
}

@State<AdvancedReportShareDialogStateModel>({
    name: 'advanced_report_share_dialog',
    defaults: DEFAULTS,
})
@Injectable()
export class AdvancedReportShareDialogState {

    @Selector()
    static data(state: AdvancedReportShareDialogStateModel) {
        return state.data;
    }

    constructor(
        private _reportService: AdvancedReportService,
    ) {}

    @Action(InitAdvancedReportShareDialog)
    initAdvancedReportShareDialog(ctx: StateContext<AdvancedReportShareDialogStateModel>, action: InitAdvancedReportShareDialog) {
        ctx.setState({
            ...DEFAULTS,
            key: action.key,
            ownerOrgKey: action.ownerOrgKey,
            status: Status.LOADING,
        });

        let state = ctx.getState();

        return this._reportService.get(state.key)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            status: Status.OK,
                            data: result,
                        });

                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error),
                        });
                    }
                )
            )
    }

    @Action(SubmitShareDialogData)
    submitShareDialogData(ctx: StateContext<AdvancedReportShareDialogStateModel>, action: SubmitShareDialogData) {
        let state = ctx.getState();

        const report: AdvancedReport = {
            ...state.data,
            access: action.data.access,
        };

        ctx.patchState({
            status: Status.LOADING,
        });

        return this._reportService.share(report.key, report, action.data.emails)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            status: Status.COMPLETE
                        });
                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error),
                        });
                    }
                )
            )
    }
}